import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  FaUserCircle,
  FaSignOutAlt,
  FaSignInAlt,
  FaShieldAlt,
} from "react-icons/fa";

import logo from "../assets/LightIdeaPlayerLogo.png";

const PLAYER_THEME_STORAGE_KEY = "lip_player_theme";
const VALID_THEMES = ["anger", "cinematic", "cool"];

function getSavedTheme() {
  const saved =
    typeof window !== "undefined"
      ? window.localStorage.getItem(PLAYER_THEME_STORAGE_KEY)
      : null;

  return VALID_THEMES.includes(saved) ? saved : "anger";
}

export default function Header({ session }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [themeKey, setThemeKey] = useState(getSavedTheme);

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      if (!session?.user?.id) {
        setUsername("");
        setIsAdmin(false);
        return;
      }

      const { data } = await supabase
        .from("profiles")
        .select("username, is_admin")
        .eq("id", session.user.id)
        .single();

      if (!isMounted) return;

      setUsername(data?.username || "");
      setIsAdmin(!!data?.is_admin);
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [session]);

  useEffect(() => {
    const syncTheme = (event) => {
      const nextTheme =
        event?.detail?.themeKey ||
        window.localStorage.getItem(PLAYER_THEME_STORAGE_KEY);

      setThemeKey(VALID_THEMES.includes(nextTheme) ? nextTheme : "anger");
    };

    window.addEventListener("player-theme-change", syncTheme);
    window.addEventListener("storage", syncTheme);

    return () => {
      window.removeEventListener("player-theme-change", syncTheme);
      window.removeEventListener("storage", syncTheme);
    };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const navClass = ({ isActive }) =>
    `rounded-full px-4 py-2 text-sm font-medium transition ${
      isActive ? "bg-white text-black" : "text-white hover:bg-white/15"
    }`;

  const pillStyle = {
    backgroundColor: "rgba(255,255,255,0.10)",
    borderColor: "rgba(255,255,255,0.15)",
    color: "#ffffff",
  };

  return (
    <header
      className="sticky top-0 z-50 flex h-16 items-center justify-between border-b px-6 backdrop-blur-xl transition-[background-color,border-color] duration-700 ease-in-out"
      style={{
        backgroundColor: "var(--header-bg, rgba(76,1,1,0.95))",
        borderColor: "rgba(255,255,255,0.10)",
      }}
      data-theme={themeKey}
    >
      <img
        src={logo}
        alt="Light Idea Player"
        onClick={() => navigate("/home")}
        className="h-8 cursor-pointer select-none"
      />

      <div className="flex items-center gap-3">
        <NavLink to="/blogs" className={navClass}>
          Blogs
        </NavLink>

        {session ? (
          <>
            {isAdmin ? (
              <Link
                to="/admin"
                className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition hover:bg-white/15"
                style={pillStyle}
              >
                <FaShieldAlt />
                <span>Admin</span>
              </Link>
            ) : null}

            <Link
              to="/profile"
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition hover:bg-white/15"
              style={pillStyle}
            >
              <FaUserCircle />
              <span className="max-w-[120px] truncate">
                {username || "Profile"}
              </span>
            </Link>

            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition hover:bg-white/15"
              style={pillStyle}
            >
              <FaSignOutAlt />
              <span>Logout</span>
            </button>
          </>
        ) : (
          <Link
            to="/login"
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-black transition hover:scale-[1.02] hover:opacity-95"
          >
            <FaSignInAlt />
            <span>Login</span>
          </Link>
        )}
      </div>
    </header>
  );
}
